import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, api } from '@/api/client';
import { Alert, Button } from '@/components/ui';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { isNative, NATIVE_DOWNLOAD_LIMIT_BYTES, saveBlob, saveFileErrorCode, type SavedFile } from '@/native';
import { SavedFileNotice } from './SavedFileNotice';

/**
 * 下载按钮（CR-043，改自 CR-031 的 ExportButton）。
 *
 * 走 `api.download`：带登录态、401 自动刷新、错误码翻译 —— 改之前 ExportButton 自己 fetch，
 * 这些一样都没有。文件名由服务端的 `Content-Disposition` 决定，页面不用再拼一遍；
 * 带上界面语言 `lang`，包名和表格名跟着它是中文还是英文（CR-051）。
 * 交给用户的那一步在 `native/download`：浏览器另存为；App 存进手机的「下载」文件夹，
 * 按钮下面出一条「已存到…」和「打开」「分享」（CR-054）。
 *
 * 按钮本身不判权限：放不放由页面按 `X.export` 决定（路由表里登记为 `gated`）。
 */
export function DownloadButton({
  path,
  query,
  label,
  action,
  variant = 'secondary',
}: {
  path: string;
  query?: Record<string, string | number | boolean | undefined>;
  label: string;
  /** 走查用的 data-action 标记 */
  action: string;
  variant?: 'primary' | 'secondary' | 'ghost';
}) {
  const { t, i18n } = useTranslation();
  const toMessage = useErrorMessage();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<SavedFile | null>(null);

  async function download() {
    setError(null);
    setSaved(null);
    setBusy(true);
    try {
      const { blob, filename } = await api.download(path, {
        ...query,
        lang: i18n.language,
        // App 要把整包读进内存再写文件，先告诉服务端上限，超了直接拒（EXPORT_TOO_LARGE）
        ...(isNative ? { limitBytes: NATIVE_DOWNLOAD_LIMIT_BYTES } : {}),
      });
      setSaved(await saveBlob(blob, filename));
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'EXPORT_TOO_LARGE'
          ? t(isNative ? 'export.tooLargeNative' : 'export.tooLarge', {
              mb: Math.round(Number(err.params?.bytes ?? 0) / 1024 / 1024),
            })
          : saveFileErrorCode(err) === 'PERMISSION_DENIED'
            ? t('export.storageDenied')
            : toMessage(err),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <Button variant={variant} loading={busy} onClick={() => void download()} data-action={action}>
        {busy ? t('export.preparing') : label}
      </Button>
      {error && <Alert>{error}</Alert>}
      {saved && <SavedFileNotice file={saved} />}
    </div>
  );
}
